// @catalog F.030
// TabSistema — 4 grupos de info técnica del producto, equivalentes al
// "Análisis post-migración" + "Identificadores" del dialog viejo, pero
// separados en cards para no contaminar la decisión de compra.

import { Box, Stack, Typography } from "@mui/material";
import type { ProductMapping } from "../productDetailTypes";
import { FStat } from "./FStat";
import { DEMAND_LABELS_LONG, fmtNum } from "./helpers";

interface TabSistemaProps {
  mapping: ProductMapping;
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 1.25,
        p: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          fontWeight: 500,
          letterSpacing: "0.02em",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "14px 18px",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function TabSistema({ mapping }: TabSistemaProps) {
  return (
    <Stack spacing={1.75}>
      <Card title="Emparejamiento Odoo 16 → 18">
        <FStat
          label="Método"
          value={mapping.matchingMethod || "—"}
          help="Cómo se vinculó este producto entre Odoo 16 y 18. 'migration_reference' = vínculo directo de migración (el más confiable)."
        />
        <FStat
          label="Confianza"
          value={`${mapping.matchingConfidence}%`}
          help="Qué tan seguro es el emparejamiento. 100% = perfecto. Menos de 70% = revisar manualmente."
        />
        <FStat
          label="ID Odoo 16"
          value={mapping.productId16 != null ? String(mapping.productId16) : "—"}
          help="Número interno del producto en el sistema anterior."
        />
        <FStat
          label="ID Odoo 18"
          value={mapping.productId18 != null ? String(mapping.productId18) : "—"}
          help="Número interno del producto en el sistema actual."
        />
        <FStat
          label="Activo en 16"
          value={mapping.isActive16 ? "Sí" : "No"}
        />
        <FStat
          label="Activo en 18"
          value={mapping.isActive18 ? "Sí" : "No"}
        />
      </Card>

      <Card title="Clasificación de demanda">
        <FStat
          label="Patrón (SBC)"
          value={
            mapping.demandClassification
              ? (DEMAND_LABELS_LONG[mapping.demandClassification] ??
                mapping.demandClassification)
              : "—"
          }
          help="Smooth=ventas constantes, Erratic=cantidades impredecibles, Intermittent=meses sin venta, Lumpy=ambos."
        />
        <FStat
          label="ABC"
          value={mapping.abcClassification ?? "—"}
          help="Clasificación por valor de venta. A=top 20%, B=medio, C=bottom 50%."
        />
        <FStat
          label="XYZ"
          value={mapping.xyzClassification ?? "—"}
          help="Predictibilidad de la demanda. X=muy predecible, Y=moderado, Z=impredecible."
        />
        <FStat
          label="Segmento"
          value={mapping.abcXyzSegment ?? "—"}
          help="Combinación ABC + XYZ. AX=alto valor y predecible (ideal); CZ=bajo valor e impredecible."
        />
        <FStat
          label="ADI"
          value={
            mapping.demandAdi != null ? mapping.demandAdi.toFixed(2) : "—"
          }
          help="Intervalo promedio entre ventas. <1.32 = demanda regular, ≥1.32 = esporádica."
        />
        <FStat
          label="CV²"
          value={
            mapping.demandCv2 != null ? mapping.demandCv2.toFixed(3) : "—"
          }
          help="Variabilidad de las cantidades. <0.49 = estable, ≥0.49 = variable."
        />
      </Card>

      <Card title="Parámetros de inventario">
        <FStat
          label="Safety stock"
          value={fmtNum(mapping.safetyStock)}
          help="Inventario mínimo de seguridad para cubrir variaciones inesperadas."
        />
        <FStat
          label="Punto de reorden"
          value={fmtNum(mapping.reorderPoint)}
          help="Cuando el stock baja a este nivel hay que pedir más. Incluye el safety stock."
        />
        <FStat
          label="Cant. compra (auto)"
          value={
            mapping.purchaseCant != null ? String(mapping.purchaseCant) : "—"
          }
          help="Cantidad por paquete detectada del nombre del producto."
        />
        <FStat
          label="Cant. compra (usuario)"
          value={
            mapping.purchaseCantUser != null
              ? String(mapping.purchaseCantUser)
              : "—"
          }
          help="Override manual desde el Decision Card. Prioridad sobre el valor automático."
        />
      </Card>

      <Card title="Identificadores">
        <FStat
          label="Unified ID"
          value={
            <Box
              component="code"
              sx={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--ink-2)",
                wordBreak: "break-all",
              }}
            >
              {mapping.unifiedProductId}
            </Box>
          }
        />
        <FStat
          label="Código"
          value={
            <Box
              component="span"
              sx={{ fontFamily: "var(--font-mono)", fontSize: 12.5 }}
            >
              {mapping.unifiedCode || "—"}
            </Box>
          }
        />
        <FStat
          label="Barcode"
          value={
            <Box
              component="span"
              sx={{ fontFamily: "var(--font-mono)", fontSize: 12.5 }}
            >
              {mapping.unifiedBarcode || "—"}
            </Box>
          }
        />
        <FStat label="Cat. Odoo 16" value={mapping.category16 || "—"} />
        <FStat label="Cat. Odoo 18" value={mapping.category18 || "—"} />
        <FStat
          label="Cat. LLM"
          value={mapping.unifiedCategory || "—"}
          {...(mapping.categoryDescription
            ? { help: mapping.categoryDescription }
            : {})}
        />
      </Card>
    </Stack>
  );
}

export default TabSistema;
